/* eslint-disable complexity */
import React from 'react';
import i18next from 'i18next';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import cn from 'classnames';

import { DATA_TEST_ID } from '~constants/tests';
import { arrayHasLength } from '~utils/array';
import SectionTitle from '~components/SectionTitle';
import { actionCreators as ModalActions } from '~redux/Modal/actions';
import { MODALS } from '~redux/Modal/constants';
import CustomModal from '~components/CustomModal';
import ToogleSwitch from '~components/ToogleSwitch';
import Select from '~components/Select';
import LocalStorageService from '~services/LocalStorageService';
import useCheckStoreOpen from '~hooks/useCheckStoreOpen';
import { noHoursAvailable } from '~redux/SearchStore/utils';
import OrderActions from '~redux/Order/actions';
import CartActions from '~redux/Cart/actions';

import useFutureOrderModal from './hooks/useFutureOrderModal';
import styles from './styles.module.scss';
import InfoBox from './components/InfoBox';

const FutureOrderModal = () => {
  const dispatch = useDispatch();
  const {
    handleSelectHours,
    handleSelectTime,
    selectedTime,
    selectedDate,
    parsedOptions,
    timeOptions,
    timeIsAvailable,
    unavailableFutureHours,
    setIsScheduled,
    isScheduled,
    showCancelBtn,
    shouldOpenProductDetails
  } = useFutureOrderModal();

  const { onlyScheduledOrders } = useCheckStoreOpen();
  const open = useSelector((state) => state.modal[`${MODALS.FUTURE_ORDER}ModalIsOpen`]);
  const handleConfirm = () => {
    if (selectedDate && selectedTime && isScheduled) {
      dispatch(
        OrderActions.setFutureOrderDate({
          date: selectedDate,
          time: selectedTime
        })
      );
      dispatch(CartActions.checkItemsAvailability());
      LocalStorageService.setFutureOrderDate({
        date: selectedDate,
        time: selectedTime,
        expiryTime: moment(`${selectedDate} ${selectedTime}`)
      });
    } else {
      dispatch(OrderActions.setFutureOrderDate(null));
      dispatch(CartActions.checkItemsAvailability());
      LocalStorageService.removeFutureOrderDate();
    }
    onClose();
  };

  const onClose = () => {
    dispatch(OrderActions.setPreselectedFutureOrderDate(null));
    dispatch(ModalActions.closeModal(MODALS.FUTURE_ORDER));
    if (shouldOpenProductDetails) {
      dispatch(ModalActions.openModal(MODALS.PRODUCT_DETAIL));
    }
  };

  return (
    <CustomModal
      hideCloseButton={noHoursAvailable}
      shouldCloseOnOverlayClick={!noHoursAvailable}
      className={cn(styles.modal, {
        [styles.notScheduled]: !isScheduled || !arrayHasLength(parsedOptions)
      })}
      modal={MODALS.FUTURE_ORDER}
      isOpen={open}
      onClose={onClose}>
      <div className="row full-width">
        <div className="column top full-height full-width">
          <SectionTitle titleClassName="text-base" className={`m-bottom-2 ${styles.titleForm}`} noSeparator>
            {i18next.t('FutureOrder:whenYouWantYourOrder')}
          </SectionTitle>
          <ToogleSwitch
            options={[
              {
                label: i18next.t('FutureOrder:asap'),
                ['data-testid']: DATA_TEST_ID.toogleSwitchNow
              },
              {
                label: i18next.t('FutureOrder:scheduleOrder'),
                ['data-testid']: !isScheduled && DATA_TEST_ID.toogleSwitchScheduleOrder
              }
            ]}
            selectedTestId={DATA_TEST_ID.toogleSwitchScheduleOrder}
            onChange={setIsScheduled}
            value={isScheduled}
            className="m-bottom-4"
          />
          <InfoBox isScheduled={isScheduled} />
          <div
            className={cn('m-bottom-1', styles.scheduleContainer, {
              [styles.show]: isScheduled
            })}>
            {unavailableFutureHours && (
              <div className={styles.warning}>{i18next.t('FutureOrder:selectOtherOption')} </div>
            )}
            {arrayHasLength(parsedOptions) && isScheduled && (
              <>
                <label className={`m-left-3 m-bottom-2 text-base regular ${styles.label}`}>
                  {i18next.t('FutureOrder:date')}
                </label>
                <Select
                  data-testid={DATA_TEST_ID.selectDateScheduledOrder}
                  className="full-width row"
                  selectClassName={`flex-start m-bottom-2 full-width full-height bold ${styles.selectorContainer} ${styles.selectorText} ${styles.selectorBackground}`}
                  optionClassName={`${styles.selectorText} ${styles.optionBackground}`}
                  options={parsedOptions}
                  handleChange={handleSelectHours}
                  defaultValue={selectedDate || parsedOptions[0]}>
                  <div className={styles.iconContainer}>
                    <span className={`m-left-1 ${styles.icon}`} />
                  </div>
                </Select>
              </>
            )}
            {timeIsAvailable && (
              <>
                <label className={`m-left-3 m-bottom-2 text-base regular ${styles.label}`}>
                  {i18next.t('FutureOrder:time')}
                </label>
                <Select
                  data-testid={DATA_TEST_ID.selectTimeScheduledOrder}
                  className="full-width row"
                  selectClassName={`flex-start m-bottom-2 full-width full-height bold ${styles.selectorContainer} ${styles.selectorText} ${styles.selectorBackground}`}
                  optionClassName={`${styles.selectorText} ${styles.optionBackground}`}
                  options={timeOptions}
                  handleChange={handleSelectTime}
                  value={selectedTime}>
                  <div className={styles.iconContainer}>
                    <span className={`m-left-1 ${styles.icon}`} />
                  </div>
                </Select>
              </>
            )}
          </div>
          <button
            data-testid={DATA_TEST_ID.buttonConfirmScheduledOrder}
            disabled={onlyScheduledOrders && !isScheduled}
            type="button"
            onClick={handleConfirm}
            className={cn('button primary full-width', styles.actionButton)}>
            {i18next.t('Delivery:confirmButton')}
          </button>
          {showCancelBtn && (
            <button
              disabled={onlyScheduledOrders && !isScheduled}
              type="button"
              onClick={onClose}
              className={cn('button secondary full-width', styles.actionButton)}>
              {i18next.t('FutureOrder:cancel')}
            </button>
          )}
        </div>
      </div>
    </CustomModal>
  );
};

export default FutureOrderModal;
